import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import mpegts from "mpegts.js";
import { Maximize2, Minimize2, MoonStar, PlayCircle, PowerOff, RefreshCw, VideoOff } from "lucide-react";
import { api } from "@/lib/api";
import { Spinner } from "@/components/shared";
import { cn } from "@/lib/utils";

type Channel = { channel: number; name: string; camera: number; role: string; url: string };
type Device = { vehicleId: string; plate: string; imei: string; provider: string; channels: Channel[] };
type Cell = Channel & { vehicleId: string; plate: string; id: string };

const TEMPLATES: [string, string][] = [
  ["cabin", "All · Cabin"],
  ["road", "All · Road"],
  ["both", "All · Both"],
];

// Wall density: how many tiles per row. A 10-car fleet on Both is 20 streams, so small tiles matter.
const DENSITY: [number, string][] = [[1, "1×1"], [2, "2×2"], [3, "3×3"], [4, "4×4"], [6, "6×6"]];
const COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
  6: "grid-cols-3 lg:grid-cols-6",
};

/** One HTTP-FLV live stream rendered into a plain <video>. Only mounted cells pull bandwidth. */
const AUTO_STOP_MS = 5 * 60 * 1000;   // a forgotten open tab must not drain the vehicle's SIM

function FlvCell({ cell, focused, compact, live, onLive, onToggle }:
                 { cell: Cell; focused: boolean; compact?: boolean; live: boolean; onLive: (on: boolean) => void; onToggle: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<"loading" | "playing" | "error">("loading");
  const [nonce, setNonce] = useState(0);

  // Streaming runs over the vehicle's mobile data, so a cell only connects when asked to.
  useEffect(() => {
    if (!live) return;
    const t = setTimeout(() => onLive(false), AUTO_STOP_MS);
    return () => clearTimeout(t);
  }, [live, onLive]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !cell.url || !live) return;
    if (!mpegts.getFeatureList().mseLivePlayback) { setState("error"); return; }
    setState("loading");
    const player = mpegts.createPlayer(
      { type: "flv", isLive: true, url: cell.url, hasAudio: false },
      { enableWorker: true, liveBufferLatencyChasing: true, lazyLoad: false, stashInitialSize: 128 },
    );
    player.attachMediaElement(el);
    player.on(mpegts.Events.ERROR, () => setState("error"));
    player.load();
    void Promise.resolve(player.play()).catch(() => {});
    const onPlaying = () => setState("playing");
    el.addEventListener("playing", onPlaying);
    return () => {
      el.removeEventListener("playing", onPlaying);
      try { player.destroy(); } catch { /* already gone */ }
    };
  }, [cell.url, nonce, live]);

  return (
    <div data-testid={`wall-cell-${cell.plate}-${cell.role}`}
         className={cn("relative bg-black rounded-md overflow-hidden border border-[#26334d] group", focused && "ring-1 ring-blue-500/60")}>
      <video ref={ref} muted playsInline className="w-full aspect-video object-contain bg-black" />
      {!live && (
        <button onClick={() => onLive(true)} data-testid={`wall-connect-${cell.plate}-${cell.role}`}
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[#070b13]/95 hover:bg-[#0d1422] transition-colors">
          <PlayCircle className="h-7 w-7 text-blue-400" />
          <span className="font-mono text-[10px] text-slate-300">Connect</span>
          {!compact && <span className="font-mono text-[9px] text-slate-600">uses the vehicle&apos;s mobile data</span>}
        </button>
      )}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center gap-2 px-2 py-1 bg-gradient-to-b from-black/80 to-transparent">
        <span className={cn("font-mono text-slate-100", compact ? "text-[9px]" : "text-[11px]")}>{cell.plate}</span>
        {!compact && <span className="font-mono text-[10px] text-slate-400">{cell.role === "cabin" ? "Cabin" : "Road"} · {cell.name}</span>}
        {compact && <span className="font-mono text-[9px] text-slate-400">{cell.role === "cabin" ? "C" : "R"}</span>}
        <span className="ml-auto" />
        {live && state === "playing" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        {live && state === "loading" && <span className="font-mono text-[10px] text-amber-400">connecting…</span>}
        {live && state === "error" && (
          <button onClick={() => setNonce((n) => n + 1)} className="font-mono text-[10px] text-rose-400 flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />retry
          </button>
        )}
        {live && (
          <button onClick={() => onLive(false)} data-testid={`wall-stop-${cell.plate}-${cell.role}`}
                  className="text-slate-400 hover:text-rose-300" title="Stop streaming">
            <PowerOff className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={onToggle} data-testid={`wall-focus-${cell.plate}-${cell.role}`}
                className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
          {focused ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function LiveWall() {
  const [template, setTemplate] = useState("both");
  const [vehicleId, setVehicleId] = useState("all");
  const [focus, setFocus] = useState<string | null>(null);
  const [density, setDensity] = useState<number>(() => Number(localStorage.getItem("tg.wall.density")) || 3);
  useEffect(() => { try { localStorage.setItem("tg.wall.density", String(density)); } catch { /* private mode */ } }, [density]);
  // Which cells are actually pulling video. Nothing streams until asked: every frame costs
  // the vehicle's mobile data, so the wall opens cold and each tile is connected on demand.
  const [liveIds, setLiveIds] = useState<Set<string>>(new Set());
  const setLive = useCallback((id: string, on: boolean) => setLiveIds((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  }), []);

  const streams = useQuery({
    queryKey: ["cctv-live-streams"],
    queryFn: async () => (await api.get("/cctv/live-streams")).data,
    refetchInterval: 240000,          // stream URLs are signed and time-boxed
    refetchOnWindowFocus: false,
  });

  const devices: Device[] = useMemo(() => streams.data?.devices || [], [streams.data]);
  const unavailable: any[] = streams.data?.unavailable || [];
  const refresh = async () => {
    const r = await streams.refetch();
    const n = (r.data?.devices || []).reduce((a: number, d: Device) => a + d.channels.length, 0);
    toast[n ? "success" : "info"](n ? `${n} live camera${n === 1 ? "" : "s"} reconnected` : "No camera is streaming right now");
  };

  const cells: Cell[] = useMemo(() => {
    const all = devices.flatMap((d) =>
      d.channels.map((c) => ({ ...c, vehicleId: d.vehicleId, plate: d.plate, id: `${d.imei}:${c.channel}` })));
    const byVehicle = vehicleId === "all" ? all : all.filter((c) => c.vehicleId === vehicleId);
    if (template === "both") return byVehicle;
    return byVehicle.filter((c) => c.role === template);
  }, [devices, template, vehicleId]);

  const shown = focus ? cells.filter((c) => c.id === focus) : cells;
  const cols = focus ? "grid-cols-1" : COLS[density] || COLS[3];
  const compact = !focus && density >= 4;

  if (streams.isLoading) return <Spinner />;
  if (!devices.length && !unavailable.length) return null;

  return (
    <div className="mb-5" data-testid="cctv-live-wall">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex rounded-md border border-[#26334d] overflow-hidden" data-testid="wall-template">
          {TEMPLATES.map(([v, l]) => (
            <button key={v} onClick={() => { setTemplate(v); setFocus(null); }} data-testid={`wall-template-${v}`}
                    className={cn("font-mono text-[11px] px-3 py-1.5", template === v ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>{l}</button>
          ))}
        </div>
        {devices.length > 1 && (
          <select value={vehicleId} onChange={(e) => { setVehicleId(e.target.value); setFocus(null); }} data-testid="wall-vehicle"
                  className="h-8 rounded-md bg-[#0f141f] border border-[#26334d] font-mono text-[11px] px-2 text-slate-200">
            <option value="all">All vehicles</option>
            {devices.map((d) => <option key={d.vehicleId} value={d.vehicleId}>{d.plate}</option>)}
          </select>
        )}
        <div className="flex rounded-md border border-[#26334d] overflow-hidden" data-testid="wall-density">
          {DENSITY.map(([n, l]) => (
            <button key={n} onClick={() => { setDensity(n); setFocus(null); }} data-testid={`wall-density-${n}`}
                    className={cn("font-mono text-[11px] px-2.5 py-1.5", density === n ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>{l}</button>
          ))}
        </div>
        <button onClick={() => setLiveIds(liveIds.size ? new Set() : new Set(shown.map((c) => c.id)))}
                data-testid="wall-toggle-all"
                className={cn("flex items-center gap-1.5 font-mono text-[11px] border rounded-md px-2.5 py-1.5",
                              liveIds.size ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                                           : "border-[#26334d] text-slate-300 hover:text-slate-100")}>
          {liveIds.size ? <><PowerOff className="h-3 w-3" />Stop all ({liveIds.size})</> : <><PlayCircle className="h-3 w-3" />Connect all</>}
        </button>
        <button onClick={refresh} disabled={streams.isFetching} data-testid="wall-refresh"
                className="flex items-center gap-1.5 font-mono text-[11px] text-slate-400 hover:text-slate-100 border border-[#26334d] rounded-md px-2.5 py-1.5 disabled:opacity-50">
          <RefreshCw className={cn("h-3 w-3", streams.isFetching && "animate-spin")} />
          {streams.isFetching ? "Refreshing…" : "Refresh"}
        </button>
        {focus && (
          <button onClick={() => setFocus(null)} data-testid="wall-exit-focus"
                  className="font-mono text-[11px] text-slate-400 hover:text-slate-100 border border-[#26334d] rounded-md px-3 py-1.5">
            Back to wall
          </button>
        )}
        <span className="font-mono text-[10px] text-slate-500 ml-auto">
          {liveIds.size ? `${liveIds.size} streaming · auto-stops after 5 min` : `${shown.length} available · nothing streaming`}
        </span>
      </div>
      {!shown.length ? (
        <div className="tg-card px-4 py-5 space-y-2" data-testid="wall-empty">
          {cells.length > 0 ? (
            <div className="flex items-center gap-2 text-slate-400 font-mono text-xs">
              <VideoOff className="h-4 w-4" />
              No camera matches “{TEMPLATES.find(([v]) => v === template)?.[1]}”.
              <button onClick={() => setTemplate("both")} className="text-blue-300 hover:underline">Show all</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-slate-300 font-mono text-xs">
                <MoonStar className="h-4 w-4 text-amber-400" />No camera is streaming right now.
              </div>
              {unavailable.map((u) => (
                <div key={u.imei} className="font-mono text-[11px] text-slate-500 pl-6">
                  <span className="text-slate-300">{u.plate}</span> — {u.reason}
                  {u.lastSeen && <> · last packet {new Date(u.lastSeen).toLocaleString()}</>}
                </div>
              ))}
              <div className="font-mono text-[10px] text-slate-600 pl-6">
                Cameras only stream while the vehicle is awake. Press Refresh once it moves.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={cn("grid gap-3", cols)}>
          {shown.map((c) => (
            <FlvCell key={c.id} cell={c} focused={focus === c.id} compact={compact}
                     live={liveIds.has(c.id)} onLive={(on) => setLive(c.id, on)}
                     onToggle={() => setFocus(focus === c.id ? null : c.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
